import type { Client } from "@langchain/langgraph-sdk";

import { threadStateCache } from "@/agents-v2/chat-runtime/thread-state-cache";
import type { AgentThreadMetadata } from "@/agents-v2/chat-runtime/types";

export const asMetadata = (metadata: unknown): AgentThreadMetadata =>
  metadata && typeof metadata === "object" && !Array.isArray(metadata)
    ? (metadata as AgentThreadMetadata)
    : {};

/**
 * Whether a thread has been archived. The flag is written as a boolean but
 * survives round-trips through metadata stores as the string "true", so both
 * spellings count.
 * @param metadata - The thread's raw metadata.
 * @returns True when the thread is archived.
 */
export const isArchivedThread = (metadata: unknown): boolean => {
  const { archived } = asMetadata(metadata);
  return archived === true || archived === "true";
};

/**
 * Whether the creator stamped this thread as the one that built the bid.
 * @param metadata - The thread's raw metadata.
 * @returns True on the bid's creation thread.
 */
export const isCreationThread = (metadata: unknown): boolean => {
  const { creation } = asMetadata(metadata);
  return creation === true || creation === "true";
};

export const withAgentScope = (
  assistantId: string,
  metadata?: AgentThreadMetadata
): AgentThreadMetadata => ({
  ...metadata,
  graph_id: metadata?.graph_id ?? assistantId
});

export const mergeThreadMetadata = async (
  client: Client,
  threadId: string,
  assistantId: string,
  patch: AgentThreadMetadata
) => {
  const thread = await client.threads.get(threadId);
  const metadata = withAgentScope(assistantId, {
    ...asMetadata(thread.metadata),
    ...patch
  });
  const updated = await client.threads.update(threadId, { metadata });
  threadStateCache.noteThreadRecord(threadId, updated);
  return metadata;
};
